import { DrawerFooterButtonStyled, DrawerSubHeaderStyled } from '@bechtel/client-common';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  FormControl,
  Heading,
  Icon,
  IconSize,
  Select,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { SortOrder } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Field, Form, Formik } from 'formik';
import { find, isEqual } from 'lodash';
import { FC } from 'react';
import { userDrawerDefaultValues as defaultValues, sortByList } from '../../form-state';
import FacilityList from './facility-list';
import RoleList from './role-list';
import { ButtonBox, DrawerBackdropStyled, DrawerOuterStyled, Header, ResetButtonStyled } from './styled';
import { UserSearchDrawerProps } from './types';
import UserStatus from './user-status';

const SortSelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {i18n._(item.label)}
    </Text>
  );
};

const SortItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {i18n._(item.label)}
    </Text>
  );
};

export const UserSearchDrawer: FC<UserSearchDrawerProps> = ({ close, onSubmit, initialValues }) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        close();
        onSubmit(values);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ setFieldValue, values, isValid, dirty, setValues }) => {
        const isAsc = values.sortOrder === SortOrder.ASC;
        return (
          <Form>
            <DrawerBackdropStyled data-testid="user-card-search-canvas-wrap">
              <DrawerOuterStyled>
                <Header>
                  <Box py={{ sm: '400' }}>
                    <Heading size="600" color="grey.1000" weight="600">
                      <Trans>Filters</Trans>
                    </Heading>
                  </Box>
                  <Box py={{ sm: '200' }}>
                    <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.TERTIARY} onClick={close}>
                      <Icon size={IconSize.LARGE} icon="times" fillColor="blue.800" />
                    </Button>
                  </Box>
                </Header>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <Box px={{ sm: '400' }} py={{ sm: '500' }}>
                      <Box mr={{ sm: '400' }} flexGrow={{ sm: 1 }} data-testid="user-card-search-canvas-sort-by">
                        <FormControl style={{ flexGrow: 1 }} label={t`Sort By`}>
                          <Select
                            data-testid="sort-by"
                            size={TextFieldSize.LARGE}
                            appearance={TextFieldAppearance.NORMAL}
                            items={sortByList}
                            itemComponent={SortItem}
                            selectedItem={find(sortByList, { value: values.sortBy })}
                            selectedComponent={SortSelectedItem}
                            onSelectedItemChange={(changes: any) => {
                              setFieldValue('sortBy', changes.selectedItem.value);
                            }}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <Button
                          appearance={ButtonAppearance.SECONDARY}
                          size={ButtonSize.LARGE}
                          onClick={() => {
                            const newValue = isAsc ? SortOrder.DESC : SortOrder.ASC;
                            setFieldValue('sortOrder', newValue);
                          }}
                        >
                          <Icon
                            fillColor="blue.800"
                            data-testid="user-card-search-canvas-sort-icon"
                            icon={isAsc ? 'sort-amount-down' : 'sort-amount-up'}
                            size={IconSize.MEDIUM}
                          />
                        </Button>
                      </Box>
                    </Box>
                    <DrawerSubHeaderStyled>
                      <Trans>Filter By</Trans>
                    </DrawerSubHeaderStyled>
                    <Field name="roleList" component={RoleList} />
                    <Field name="facilityList" component={FacilityList} />
                    <Field name="userStatus" component={UserStatus} />
                    {/* TODO: Remove MockedProvider once backend implement company */}
                    {/* <Field name="companyList" component={CompanyList} /> */}
                    <ResetButtonStyled>
                      <Button
                        data-testid="user-card-search-canvas-reset"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        type="reset"
                        disabled={isEqual(defaultValues, values)}
                        onClick={() => {
                          setValues(defaultValues);
                        }}
                      >
                        <Trans>Reset All Filters</Trans>
                      </Button>
                    </ResetButtonStyled>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <ButtonBox>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={close}
                        data-testid="user-card-search-canvas-cancel"
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        type="submit"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!dirty || !isValid}
                        data-testid="user-card-search-canvas-apply-filter"
                      >
                        <Trans>Apply Filters</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  </ButtonBox>
                </SideSheetFooter>
              </DrawerOuterStyled>
            </DrawerBackdropStyled>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserSearchDrawer;
